// src/services/datev/DatevExchangeService.ts

import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';
import { leseEdgeFunctionFehler } from '../../shared/utils/edgeFunctionFehler';

// ============================================
// TYPESCRIPT INTERFACES (basierend auf hr:exchange OpenAPI v1.0.21)
// ============================================

export type DatevTargetSystem = 'lug' | 'lodas';
export type DatevJobState = 'accepted' | 'in_progress' | 'successful' | 'failed';

export interface DatevExchangeJob {
  id: string;                    // job-uuid
  state: DatevJobState;
  time_stamp: string;
  time_stamp_updated: string;
}

// ============================================
// JOB-REQUEST-BODIES (Phase 1: POST /jobs)
// ============================================

export interface DatevReadJobRequest {
  resource_name: 'client-data' | 'employees' | 'employment-periods' | 'gross-payments' |
                  'hourly-wages' | 'absences' | 'month-records' | 'individual-data';
  reference_date?: string;       // Format "YYYY-MM"
  id?: string | number;          // z.B. Personalnummer bei resource_name="employees"
  sub_resource?: {
    resource_name: string;
    reference_date?: string;
  };
}

// ============================================
// EMPLOYEE — Mitarbeiterdaten (Lesen + Schreiben)
// ============================================

export interface DatevExchangeEmployeePersonalData {
  sex?: 'M' | 'W' | 'D';         // Männlich, Weiblich, Divers
  date_of_birth?: string;
  nationality?: string;
  email?: string;
  phone?: string;
  social_security_number?: string;
  [key: string]: any;
}

export interface DatevExchangeEmploymentPeriod {
  date_of_commencement_of_employment: string;   // Format "YYYY-MM-DD"
  date_of_termination_of_employment?: string | null;
}

export interface DatevExchangeEmployeeAddress {
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  address_affix?: string;
}

export interface DatevExchangeEmployee {
  surname: string;
  first_name: string;
  personnel_number?: number;     // Optional bei POST (System vergibt automatisch)
  company_personnel_number?: string | null;
  business_unit_id?: number;
  payment_method?: string;
  personal_data?: DatevExchangeEmployeePersonalData;
  employment_periods?: DatevExchangeEmploymentPeriod | DatevExchangeEmploymentPeriod[];
  address?: DatevExchangeEmployeeAddress;
  activity?: any;
  account?: any;
  gross_payments?: any[];
  social_insurance?: any;
  tax_card?: any;
  taxation?: any;
  vacation_entitlement?: any;
  vocational_training?: any;
  [key: string]: any;
}

// ============================================
// JOB-RESULT-RESPONSES
// ============================================

export interface DatevExchangeJobResult<T> {
  errors: DatevExchangeError[];
  [key: string]: T[] | DatevExchangeError[] | any;
}

export interface DatevExchangeError {
  error: string;
  error_description?: string;
  error_uri?: string;
  request_id?: string | null;
  additional_messages?: Array<{
    id: string;
    severity: string;
    description: string;
    path?: string;
    affected_fields?: string[];
  }>;
}

// Frontend-Fehler (vom Backend zurückgegeben)
export interface DatevExchangeApiError {
  error: string;
  datev_error?: string;
  help_url?: string;
  requires_reconnect?: boolean;
}

// Wrapper für Operationen mit Job-Tracking
export interface DatevExchangeOperationOptions {
  customerId: string;
  clientId: string;             // "berater-mandant" Format
  targetSystem?: DatevTargetSystem;  // Default: 'lug'
  retrievedBy?: string;
}

// ============================================
// ÄNDERUNGSAUFTRÄGE (Stufe C) — via Edge Function datev-employee-change
// ============================================

/** Ein Änderungsauftrag: alt→neu-Protokoll + exakter DATEV-PUT-Body */
export interface DatevEmployeeChangeSubmission {
  personalnummer: string;
  referenceDate: string;                                   // Format "YYYY-MM"
  changedFields: Record<string, { alt: any; neu: any }>;   // Protokoll alt→neu
  requestBody: Partial<DatevExchangeEmployee>;             // Exakter PUT-Body (nur geänderte Felder + Identifikation)
}

/** Antwort der Schreibroute bei erfolgreicher Einreichung */
export interface DatevEmployeeChangeResponse {
  change_id: string;
  job_uuid: string;
  status: 'submitted';
}

// ============================================
// SERVICE
// ============================================

class DatevExchangeService {
  private getApiBaseUrl(): string {
    return 'https://intranet.xtax.de/.netlify/functions/datev-exchange';
  }

  private buildQuery(opts: DatevExchangeOperationOptions, extra?: Record<string, string>): string {
    const params = new URLSearchParams();
    params.append('customerId', opts.customerId);
    if (opts.targetSystem) params.append('target_system', opts.targetSystem);
    if (opts.retrievedBy) params.append('retrievedBy', opts.retrievedBy);
    if (extra) {
      Object.entries(extra).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.append(k, v);
      });
    }
    return '?' + params.toString();
  }

  private async fetchJson<T>(method: string, path: string, query: string, body?: any): Promise<T> {
    const init: RequestInit = {
      method: method,
      headers: body ? { 'Content-Type': 'application/json' } : {}
    };
    if (body) init.body = JSON.stringify(body);

    const response = await netlifyFetch(`${this.getApiBaseUrl()}${path}${query}`, init);
    if (!response.ok) {
      const error: DatevExchangeApiError = await response.json();
      throw error;
    }
    // Bei leerem Body (z.B. 200 ohne Content): leeres Objekt zurückgeben
    const text = await response.text();
    if (!text) return {} as T;
    return JSON.parse(text) as T;
  }

  // ============================================
  // BERECHTIGUNGSPRÜFUNG
  // ============================================

  /** GET /clients/{client-id} — Berechtigungsprüfung (DATEV MUST: vor allem anderen) */
  async checkAccess(opts: DatevExchangeOperationOptions): Promise<{ authorized: boolean }> {
    try {
      await this.fetchJson<any>('GET', `/clients/${opts.clientId}`, this.buildQuery(opts));
      return { authorized: true };
    } catch (err) {
      throw err;
    }
  }

  // ============================================
  // JOBS — Lesen via 3-Phasen-Modell
  // ============================================

  /** Phase 1: POST /clients/{client-id}/jobs — Lese-Job anlegen */
  async createReadJob(
    opts: DatevExchangeOperationOptions,
    request: DatevReadJobRequest
  ): Promise<DatevExchangeJob> {
    return this.fetchJson<DatevExchangeJob>(
      'POST',
      `/clients/${opts.clientId}/jobs`,
      this.buildQuery(opts),
      request
    );
  }

  /** Phase 2: GET /clients/{client-id}/jobs/{uuid} — Job-Status pollen
   *  DATEV MUST: max 1 Request pro Minute, max 15 Min insgesamt
   */
  async getJobStatus(
    opts: DatevExchangeOperationOptions,
    jobUuid: string
  ): Promise<DatevExchangeJob> {
    return this.fetchJson<DatevExchangeJob>(
      'GET',
      `/clients/${opts.clientId}/jobs/${jobUuid}`,
      this.buildQuery(opts)
    );
  }

  /** Phase 3: GET /clients/{client-id}/jobs/{uuid}/result/{resource} — Ergebnis abholen */
  async getJobResult<T = any>(
    opts: DatevExchangeOperationOptions,
    jobUuid: string,
    resourceType: string
  ): Promise<DatevExchangeJobResult<T>> {
    return this.fetchJson<DatevExchangeJobResult<T>>(
      'GET',
      `/clients/${opts.clientId}/jobs/${jobUuid}/result/${resourceType}`,
      this.buildQuery(opts)
    );
  }

  // ============================================
  // SCHREIBEN — Mitarbeiter anlegen
  // ============================================

  /** POST — Neuen Mitarbeiter anlegen
   *  Läuft seit dem Schreibrouten-Schutz über die Edge Function
   *  datev-employee-change (action 'create'): personengebunden via
   *  Identitäts-Token, Write-Secret bleibt serverseitig. Signatur und
   *  Rückgabe sind unverändert — DATEV gibt 202 Accepted + job-uuid zurück,
   *  danach wie bisher via getJobStatus/pollJobUntilDone pollen.
   *  Reference-Date Format "YYYY-MM" (DATEV-Pflicht).
   */
  async createEmployee(
    opts: DatevExchangeOperationOptions,
    employee: DatevExchangeEmployee | DatevExchangeEmployee[],
    referenceDate: string
  ): Promise<DatevExchangeJob> {
    const identityToken = localStorage.getItem('authToken');
    if (!identityToken) {
      const identityError = new Error('Keine Anmelde-Identität gefunden — bitte im Intranet neu einloggen.') as Error & DatevExchangeApiError;
      identityError.error = 'Keine Anmelde-Identität gefunden — bitte im Intranet neu einloggen.';
      throw identityError;
    }

    const { data, error } = await supabase.functions.invoke('datev-employee-change', {
      body: {
        action: 'create',
        identity_token: identityToken,
        customer_id: opts.customerId,
        client_id: opts.clientId,
        target_system: opts.targetSystem || 'lug',
        reference_date: referenceDate,
        request_body: employee
      }
    });

    if (error) {
      // FunctionsHttpError: Fehler-Body der Edge Function auslesen (falls
      // vorhanden), über den gemeinsamen Helfer edgeFunctionFehler.
      const parsed = (await leseEdgeFunctionFehler(error)) as DatevExchangeApiError | null;
      const message = (parsed && parsed.error) || error.message || 'Mitarbeiter-Anlage fehlgeschlagen';
      const createError = new Error(message) as Error & DatevExchangeApiError;
      createError.error = message;
      if (parsed) {
        createError.datev_error = parsed.datev_error;
        createError.help_url = parsed.help_url;
        createError.requires_reconnect = parsed.requires_reconnect;
      }
      throw createError;
    }

    return data as DatevExchangeJob;
  }

  // ============================================
  // SCHREIBEN — Mitarbeiter ändern
  // ============================================

  /** PUT /clients/{client-id}/employees/{personnel-number} — Bestehenden Mitarbeiter ändern
   *  DATEV-Vertrag laut Technischer Einführung: PUT zum Aktualisieren.
   *  Gibt wie die Anlage 202 Accepted + job-uuid zurück, dann via getJobStatus pollen.
   *  Reference-Date Format "YYYY-MM" (analog zur Anlage als Query-Parameter).
   *  Hinweis: Verb und Pfad werden im Systematik-Test am Produktiv-Testmandanten
   *  empirisch verifiziert, bevor die Funktion in die Breite geht.
   */
  async updateEmployee(
    opts: DatevExchangeOperationOptions,
    personnelNumber: number | string,
    employee: DatevExchangeEmployee,
    referenceDate: string
  ): Promise<DatevExchangeJob> {
    const query = this.buildQuery(opts, { reference_date: referenceDate });
    return this.fetchJson<DatevExchangeJob>(
      'PUT',
      `/clients/${opts.clientId}/employees/${encodeURIComponent(String(personnelNumber))}`,
      query,
      employee
    );
  }

  // ============================================
  // SCHREIBEN — Änderungsauftrag (Stufe C)
  // ============================================

  /** Reicht einen protokollierten Änderungsauftrag über die Edge Function
   *  datev-employee-change ein (personengebunden: nur mit gültiger
   *  Supabase-Auth-Session; initiated_by wird SERVERSEITIG aus dem
   *  eingeloggten Mitarbeiter gesetzt).
   *
   *  Anders als updateEmployee wird hier NICHT auf den DATEV-Job gewartet:
   *  Die Antwort ist der Auftrag (change_id, job_uuid, status 'submitted');
   *  die Bestätigung (confirmed/failed) kommt über den DATEV-Rückkanal
   *  (datev-exchange-callback, geerntet von datev-employee-change-worker) und
   *  ist in datev_employee_changes nachlesbar.
   */
  async submitEmployeeChange(
    opts: DatevExchangeOperationOptions,
    change: DatevEmployeeChangeSubmission
  ): Promise<DatevEmployeeChangeResponse> {
    // Persönliche Identität aus dem Google-SSO-Login der App: auth-callback.js
    // signiert ein JWT (email/name, 7 Tage, bei Nutzung laufend erneuert durch
    // auth-refresh.js), das die App als 'authToken' im localStorage hält.
    // Die Edge Function verifiziert die Signatur und setzt
    // initiated_by daraus — fälschungssicher, serverseitig.
    const identityToken = localStorage.getItem('authToken');
    if (!identityToken) {
      const identityError = new Error('Keine Anmelde-Identität gefunden — bitte im Intranet neu einloggen.') as Error & DatevExchangeApiError;
      identityError.error = 'Keine Anmelde-Identität gefunden — bitte im Intranet neu einloggen.';
      throw identityError;
    }

    const { data, error } = await supabase.functions.invoke('datev-employee-change', {
      body: {
        identity_token: identityToken,
        customer_id: opts.customerId,
        client_id: opts.clientId,
        target_system: opts.targetSystem || 'lug',
        personalnummer: change.personalnummer,
        reference_date: change.referenceDate,
        changed_fields: change.changedFields,
        request_body: change.requestBody
      }
    });

    if (error) {
      // FunctionsHttpError: Fehler-Body der Edge Function auslesen (falls
      // vorhanden), über den gemeinsamen Helfer edgeFunctionFehler.
      const parsed = (await leseEdgeFunctionFehler(error)) as DatevExchangeApiError | null;
      const message = (parsed && parsed.error) || error.message || 'Änderungsauftrag fehlgeschlagen';
      const submitError = new Error(message) as Error & DatevExchangeApiError;
      submitError.error = message;
      if (parsed) {
        submitError.datev_error = parsed.datev_error;
        submitError.help_url = parsed.help_url;
        submitError.requires_reconnect = parsed.requires_reconnect;
      }
      throw submitError;
    }

    return data as DatevEmployeeChangeResponse;
  }

  // ============================================
  // CONVENIENCE: Polling mit automatischem Result-Abruf
  // ============================================

  /** Pollt einen Job bis er successful/failed ist und holt das Ergebnis automatisch.
   *  
   *  Polling-Intervalle: 60 Sek erste Wartezeit, danach 60 Sek pro Request,
   *  max 15 Min gesamt — gemäß DATEV-MUST (Statusabfrage nach mindestens 1 Minute,
   *  danach 1 Request/Minute, Abbruch nach 15 Min).
   *  
   *  State-Vergleich ist case-insensitive — DATEV liefert "SUCCESSFUL" (uppercase),
   *  obwohl die Doku "successful" (lowercase) angibt.
   *  
   *  @param onProgress Optional: Callback bei jedem State-Update (für UI-Updates)
   */
  async pollJobUntilDone(
    opts: DatevExchangeOperationOptions,
    jobUuid: string,
    resourceType: string,
    onProgress?: (state: DatevJobState, pollNumber: number, secondsElapsed: number) => void
  ): Promise<DatevExchangeJobResult<any>> {
    const POLL_INTERVAL_MS = 60_000;      // 60 Sek zwischen Pollings (DATEV-MUST)
    const MAX_DURATION_MS  = 15 * 60_000; // max 15 Min (DATEV-MUST)
    const FIRST_WAIT_MS    = 60_000;      // 60 Sek erste Wartezeit (DATEV-MUST: Statusabfrage nach mindestens 1 Minute)

    const startedAt = Date.now();
    let pollNumber = 0;

    onProgress?.('accepted', 0, 0);
    await this.sleep(FIRST_WAIT_MS);

    while (Date.now() - startedAt < MAX_DURATION_MS) {
      pollNumber++;
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const job = await this.getJobStatus(opts, jobUuid);
      const stateLower = (job.state || '').toLowerCase() as DatevJobState;
      onProgress?.(stateLower, pollNumber, elapsed);

      if (stateLower === 'successful') {
        return this.getJobResult(opts, jobUuid, resourceType);
      }
      if (stateLower === 'failed') {
        const failError = new Error('Job ist im DATEV-System fehlgeschlagen') as Error & DatevExchangeApiError;
        failError.error = 'Job ist im DATEV-System fehlgeschlagen';
        failError.datev_error = 'JOB_FAILED';
        throw failError;
      }

      // Noch nicht fertig → warten
      await this.sleep(POLL_INTERVAL_MS);
    }

    const timeoutError = new Error('Polling-Timeout: Job hat keinen Endstatus erreicht') as Error & DatevExchangeApiError;
    timeoutError.error = 'Polling-Timeout: Job hat keinen Endstatus erreicht';
    timeoutError.datev_error = 'POLL_TIMEOUT';
    throw timeoutError;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const datevExchangeService = new DatevExchangeService();
